import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = (props: React.ComponentProps<typeof DialogPrimitive.Root>) => {
  const { open, onOpenChange } = props;
  
  // Prevent aria-hidden from being applied to Lovable chat interface
  React.useEffect(() => {
    if (!open) return;
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
          const target = mutation.target as HTMLElement;
          
          // Check if this element contains the Lovable chat interface
          const hasLovableChat = target.querySelector('#chatinput') || 
                                target.querySelector('[data-lovable-chat]') ||
                                target.closest('[data-lovable-chat]');
          
          // If it has aria-hidden and contains the chat, remove it
          if (hasLovableChat && target.getAttribute('aria-hidden') === 'true') {
            console.log('🔒 Preventing aria-hidden on Lovable chat interface');
            target.removeAttribute('aria-hidden');
            target.removeAttribute('data-aria-hidden');
            
            // Also remove any inert attribute
            if ('inert' in target) {
              (target as any).inert = false;
            }
          }
        }
      });
    });
    
    // Observe all direct children of body for aria-hidden changes
    document.body.childNodes.forEach((child) => {
      if (child instanceof HTMLElement) {
        observer.observe(child, { 
          attributes: true, 
          attributeFilter: ['aria-hidden', 'data-aria-hidden'],
          subtree: false 
        });
      }
    });
    
    return () => observer.disconnect();
  }, [open]);
  
  return <DialogPrimitive.Root {...props} onOpenChange={onOpenChange} />;
}

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
      className={cn(
        "fixed inset-0 z-[1000] bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { draggable?: boolean }
>(({ className, children, draggable = true, ...props }, ref) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });

  // Reset position on mount (when dialog opens)
  React.useEffect(() => {
    setOffset({ x: 0, y: 0 });
  }, []);

  // Prevent aria-hidden from being applied to dialog content when it contains focused elements
  React.useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    // Watch for aria-hidden being applied by nested components (like Popovers)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
          const target = mutation.target as HTMLElement;
          // If aria-hidden is being set while dialog is open
          if (target.getAttribute('aria-hidden') === 'true') {
            const hasFocusedElement = document.activeElement && target.contains(document.activeElement);
            const hasFocusableElements = target.querySelector('input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
            
            // Remove aria-hidden if there are focused or focusable elements
            if (hasFocusedElement || hasFocusableElements) {
              console.warn('⚠️ Prevented aria-hidden on dialog with focusable elements');
              target.removeAttribute('aria-hidden');
              target.removeAttribute('data-aria-hidden');
              // Reset pointer events if they were disabled
              if (target.style.pointerEvents === 'none') {
                target.style.pointerEvents = '';
              }
            }
          }
        }
      });
    });

    observer.observe(element, { 
      attributes: true, 
      attributeFilter: ['aria-hidden', 'data-aria-hidden', 'style'],
      subtree: false 
    });

    return () => observer.disconnect();
  }, []);

  // Draggable behavior (drag by header if present, otherwise by content)
  React.useEffect(() => {
    if (!draggable) return;
    const el = contentRef.current;
    if (!el) return;

    const handle = (el.querySelector('[data-dialog-handle]') as HTMLElement) || el;

    let startX = 0, startY = 0;
    let startOffX = 0, startOffY = 0;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return; // left click only
      // Avoid starting drag from interactive elements inside the handle
      const target = e.target as HTMLElement;
      if (target && target.closest('button, [role="button"], a[href], input, select, textarea, [contenteditable="true"], [data-radix-select-trigger]')) {
        return;
      }

      startX = e.clientX;
      startY = e.clientY;
      startOffX = offset.x;
      startOffY = offset.y;

      handle.setPointerCapture?.(e.pointerId);

      const onPointerMove = (ev: PointerEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;

        const rect = el.getBoundingClientRect();
        const margin = 16;
        const maxDx = Math.max(0, (window.innerWidth / 2) - rect.width / 2 - margin);
        const maxDy = Math.max(0, (window.innerHeight / 2) - rect.height / 2 - margin);

        let nextX = startOffX + dx;
        let nextY = startOffY + dy;
        nextX = Math.min(maxDx, Math.max(-maxDx, nextX));
        nextY = Math.min(maxDy, Math.max(-maxDy, nextY));

        setOffset({ x: nextX, y: nextY });
      };

      const end = () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
      };

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);
    };

    // Visual cue
    const prevCursor = handle.style.cursor;
    handle.style.cursor = 'move';

    handle.addEventListener('pointerdown', onPointerDown);
    return () => {
      handle.style.cursor = prevCursor;
      handle.removeEventListener('pointerdown', onPointerDown);
    };
  }, [draggable, offset.x, offset.y]);

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={(node) => {
          // Handle both callback and object refs
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            (ref as React.MutableRefObject<any>).current = node;
          }
          (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        className={cn(
          "fixed left-[50%] top-[50%] z-[1001] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-2xl",
          className
        )}
        style={{ transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px)` }}
        onOpenAutoFocus={(e) => {
          // Ensure dialog content is not marked as aria-hidden on open
          const dialogElement = e.currentTarget as HTMLElement;
          if (dialogElement.hasAttribute('aria-hidden')) {
            dialogElement.removeAttribute('aria-hidden');
            dialogElement.removeAttribute('data-aria-hidden');
          }
          props.onOpenAutoFocus?.(e);
        }}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-dialog-handle
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
